import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'
import DayOverview from './DayOverview'
import BookingDetails from './BookingDetails'
import useResourceData from '@/hooks/useResourceData'
import useScheduleData from '@/hooks/useScheduleData'
import Loading from '@/widgets/Loading'
import CustomerPicker from '@/widgets/CustomerPicker'
import CustomerCard from '@/widgets/CustomerCard'

const toUTCNoTZ = localStr => {
	if (!localStr) return ''
	const d = new Date(localStr)
	if (Number.isNaN(d.getTime())) return ''
	const pad = n => String(n).padStart(2, '0')
	return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(
		d.getUTCMinutes()
	)}`
}

const buildDefaultFormState = (anchorDate, resources, initialCustomerId = '') => {
	const safeAnchor = anchorDate ? moment(anchorDate) : moment()
	const nearestHour = safeAnchor.clone().minutes(0).seconds(0).milliseconds(0)
	const defaultResource = resources?.[0]
	const defaultResourceId = String(defaultResource?._id ?? defaultResource?.id ?? '')

	return {
		title: '',
		service: defaultResource?.name || '',
		resourceId: defaultResourceId,
		productId: '',
		customerId: initialCustomerId,
		customerName: '',
		customerTel: '',
		start: nearestHour.format('YYYY-MM-DDTHH:00'),
		end: nearestHour.clone().add(1, 'hour').format('YYYY-MM-DDTHH:00'),
		notes: '',
	}
}

function ScheduleAddPage() {
	const navigate = useNavigate()
	const location = useLocation()
	const anchorFromState = location.state?.anchorDate || null
	const computedAnchor = useMemo(() => (anchorFromState ? moment(anchorFromState) : moment()), [anchorFromState])
	const requestedCustomerId = useMemo(() => {
		const params = new URLSearchParams(location.search)
		const rawId = params.get('customerId') ?? params.get('customerid') ?? params.get('customer_id') ?? ''
		return String(rawId || '').trim()
	}, [location.search])

	const {
		data: resourceData,
		loading: resourcesLoading,
		error: resourcesError,
	} = useResourceData()
	const safeResources = useMemo(() => (Array.isArray(resourceData) ? resourceData : []), [resourceData])

	const [formState, setFormState] = useState(() =>
		buildDefaultFormState(computedAnchor, safeResources, requestedCustomerId)
	)
	const [formError, setFormError] = useState('')
	const [initialized, setInitialized] = useState(false)
	const [dayViewDate, setDayViewDate] = useState(() => computedAnchor.clone())

	useEffect(() => {
		if (initialized) return
		if (resourcesLoading) return
		const nextState = buildDefaultFormState(computedAnchor, safeResources, requestedCustomerId)
		setFormState(nextState)
		const nextStart = moment(nextState.start)
		setDayViewDate(nextStart.isValid() ? nextStart : computedAnchor.clone())
		setInitialized(true)
	}, [initialized, resourcesLoading, computedAnchor, safeResources, requestedCustomerId])

	useEffect(() => {
		if (!formState.start) return
		const startMoment = moment(formState.start)
		if (!startMoment.isValid()) return
		setDayViewDate(prev => {
			if (prev?.isSame?.(startMoment, 'day')) return prev
			return startMoment
		})
	}, [formState.start])

	const scheduleRange = useMemo(
		() => ({
			start: dayViewDate.clone().startOf('day').toISOString(),
			end: dayViewDate.clone().endOf('day').toISOString(),
		}),
		[dayViewDate]
	)

	const {
		data: bookingsData,
		loading: bookingsLoading,
		error: bookingsError,
		create: createBooking,
		creating: creatingBooking,
	} = useScheduleData(scheduleRange.start, scheduleRange.end)
	const safeBookings = useMemo(() => (Array.isArray(bookingsData) ? bookingsData : []), [bookingsData])

	const draftBooking = useMemo(() => {
		if (!formState.resourceId || !formState.start || !formState.end) return null
		return {
			resourceId: formState.resourceId,
			start: formState.start,
			end: formState.end,
		}
	}, [formState.resourceId, formState.start, formState.end])

	const handleCustomerSelect = useCallback(customer => {
		if (!customer) return
		const name =
			customer.name ||
			customer.customerName ||
			`${customer.firstName || ''} ${customer.lastName || ''}`.trim() ||
			'Unnamed customer'
		const phone = customer.tel || customer.phone || customer.mobile || ''
		const customerId = String(customer._id ?? customer.customerid ?? customer.id ?? '')
		setFormState(prev => ({ ...prev, customerId, customerName: name, customerTel: phone }))
	}, [])

	const handleCustomerClear = useCallback(() => {
		setFormState(prev => ({ ...prev, customerId: '', customerName: '', customerTel: '' }))
	}, [])

	const handleChange = useCallback((field, value) => {
		setFormState(prev => ({ ...prev, [field]: value }))
	}, [])

	const handleServiceChange = useCallback(
		e => {
			const { value } = e.target
			setFormState(prev => {
				const selectedResource = safeResources.find(r => String(r?._id ?? r?.id ?? '') === value)
				return { ...prev, resourceId: value, service: prev.service || selectedResource?.name || '' }
			})
		},
		[safeResources]
	)

	const handleProductSelect = useCallback(product => {
		if (!product) {
			setFormState(prev => ({ ...prev, productId: '', service: '' }))
			return
		}
		const nextId = product._id ?? product.id ?? ''
		setFormState(prev => ({ ...prev, productId: String(nextId || ''), service: product.name || '' }))
	}, [])

	const handleSlotSelect = useCallback(({ resourceId, resourceName, slotMoment }) => {
		if (!resourceId || !slotMoment) return
		const normalizedStart = slotMoment.clone().seconds(0).milliseconds(0)
		setFormState(prev => ({
			...prev,
			resourceId,
			service: prev.service || resourceName || '',
			start: normalizedStart.format('YYYY-MM-DDTHH:mm'),
			end: normalizedStart.clone().add(1, 'hour').format('YYYY-MM-DDTHH:mm'),
		}))
	}, [])

	const handleDayChange = useCallback(nextValue => {
		const candidate = moment.isMoment(nextValue) ? nextValue.clone() : moment(nextValue)
		if (!candidate.isValid()) return
		setDayViewDate(candidate.clone())
		setFormState(prev => {
			const prevStart = moment(prev.start)
			const prevEnd = moment(prev.end)
			const nextStart = prevStart.isValid()
				? candidate.clone().hour(prevStart.hour()).minute(prevStart.minute())
				: candidate.clone().hour(9).minute(0)
			const durationMinutes = prevStart.isValid() && prevEnd.isValid() ? prevEnd.diff(prevStart, 'minutes') : 60
			const safeDuration = Number.isFinite(durationMinutes) && durationMinutes > 0 ? durationMinutes : 60
			const nextEnd = nextStart.clone().add(safeDuration, 'minutes')
			return {
				...prev,
				start: nextStart.format('YYYY-MM-DDTHH:mm'),
				end: nextEnd.format('YYYY-MM-DDTHH:mm'),
			}
		})
	}, [])

	const handleSubmit = useCallback(
		async e => {
			e?.preventDefault()
			setFormError('')

			const chosenResourceId = formState.resourceId || String(safeResources[0]?._id ?? safeResources[0]?.id ?? '')

			const payload = {
				title: formState.title?.trim() || formState.service || '',
				resourceId: chosenResourceId,
				productId: formState.productId,
				productName: formState.service?.trim() || undefined,
				customerId: formState.customerId,
				customerName: formState.customerName,
				customerTel: formState.customerTel,
				start: toUTCNoTZ(formState.start),
				end: toUTCNoTZ(formState.end),
				notes: formState.notes?.trim() || undefined,
			}

			try {
				const success = await createBooking(payload)
				if (success) {
					navigate('/schedule', { state: { message: 'Booking created successfully.' } })
					return
				}
				setFormError('Unable to create the booking. Please try again.')
			} catch (err) {
				setFormError(err?.message || 'Unable to create the booking. Please try again.')
			}
		},
		[formState, createBooking, navigate, safeResources]
	)

	if (resourcesLoading || bookingsLoading || !initialized) {
		return <Loading />
	}

	return (
		<div className="space-y-6">
			<div className="bg-white rounded-2xl shadow-sm p-6">
				<div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
					<div>
						<h1 className="text-2xl font-semibold text-gray-900">Add Booking</h1>
						<p className="text-sm text-gray-500">Review the day overview and fill in the booking details.</p>
					</div>
					<button
						type="button"
						onClick={() => navigate('/schedule')}
						className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
						Back to schedule
					</button>
				</div>

				{resourcesError && (
					<div className="mb-4 rounded-md bg-red-50 p-4 text-sm text-red-700">
						{resourcesError}
					</div>
				)}
				{bookingsError && (
					<div className="mb-4 rounded-md bg-yellow-50 p-4 text-sm text-yellow-800">
						{bookingsError}
					</div>
				)}

				<div className="space-y-6">
					<section className="rounded-2xl border border-gray-200 p-4">
						<div className="mb-4">
							<h3 className="text-base font-semibold text-gray-900">Customer</h3>
							<p className="text-sm text-gray-500">
								Select a customer to attach their contact information to the booking.
							</p>
						</div>
						{formState.customerId ? (
							<CustomerCard
								customerId={formState.customerId}
								handleChange={handleCustomerClear}
								onLoaded={handleCustomerSelect}
							/>
						) : (
							<CustomerPicker onCustomerSelect={handleCustomerSelect} />
						)}
					</section>

					<DayOverview
						date={dayViewDate}
						resources={safeResources}
						bookings={safeBookings}
						onSlotSelect={handleSlotSelect}
						draftBooking={draftBooking}
						onDateChange={handleDayChange}
					/>

					<BookingDetails
						formState={formState}
						formError={formError}
						onSubmit={handleSubmit}
						onFieldChange={handleChange}
						onServiceChange={handleServiceChange}
						onProductSelect={handleProductSelect}
						onCancel={() => navigate('/schedule')}
						creating={creatingBooking}
						resources={safeResources}
					/>
				</div>
			</div>
		</div>
	)
}

export default ScheduleAddPage
